/**
 * Drawer Menu Renderer
 * @description 멀티 패널 구조의 사이드바 메뉴 (카테고리 클릭 시 옆에 하위 카테고리가 쌓임)
 */

import { $, $$ } from '../../../../core';
import type { Category, SideMenuElements } from '../types';
import type { SideMenuRenderer } from './types';

// 아이콘 상수
const ICONS = {
  close: `<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13.5 4.5L4.5 13.5" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M4.5 4.5L13.5 13.5" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
  chevron: (color = 'currentColor') =>
    `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 12L10 8L6 4" stroke="${color}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
};

/**
 * 드로어 상태 관리
 */
interface DrawerState {
  drawer: HTMLElement | null;
  overlay: HTMLElement | null;
  drawerContent: HTMLElement | null;
  isDrawerOpen: boolean;
  closeTimeoutId: number | null;
  categories: Category[];
}

const state: DrawerState = {
  drawer: null,
  overlay: null,
  drawerContent: null,
  isDrawerOpen: false,
  closeTimeoutId: null,
  categories: [],
};

/**
 * 드로어 너비 업데이트
 */
const updateDrawerWidth = (): void => {
  if (!state.drawerContent || !state.drawer) {
    return;
  }

  const panels = $$<HTMLElement>('.nde-c-side-menu__mp-panel', state.drawerContent);
  let totalWidth = 0;
  for (const p of panels) {
    totalWidth += p.offsetWidth;
  }

  state.drawer.style.width = `${totalWidth}px`;

  // 뷰포트를 초과하면 자동 스크롤
  if (totalWidth > window.innerWidth) {
    state.drawerContent.scrollTo({
      left: state.drawerContent.scrollWidth,
      behavior: 'smooth',
    });
  }
};

/**
 * 드로어 닫기
 */
const closeDrawer = (): void => {
  if (!state.isDrawerOpen || !state.drawer) {
    return;
  }

  state.drawer.classList.remove('nde-c-side-menu__drawer--open');
  state.overlay?.classList.remove('nde-c-side-menu__drawer-overlay--visible');

  state.isDrawerOpen = false;

  state.closeTimeoutId = window.setTimeout(() => {
    document.body.style.overflow = '';
    if (state.drawerContent) {
      state.drawerContent.innerHTML = '';
    }
    if (state.drawer) {
      state.drawer.style.width = '';
    }
    state.closeTimeoutId = null;
  }, 300);
};

/**
 * 패널 생성
 */
/**
 * 패널 상단 영역 생성 (첫 번째 패널: 헤더, 이후: 스페이서)
 */
const createPanelTopArea = (isFirstPanel: boolean): HTMLElement => {
  if (isFirstPanel) {
    const headerContainer = document.createElement('div');
    headerContainer.className = 'nde-c-side-menu__mp-header';

    const title = document.createElement('h2');
    title.className = 'nde-c-side-menu__mp-title';
    title.textContent = '카테고리';

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'nde-c-side-menu__mp-close';
    closeBtn.innerHTML = ICONS.close;
    closeBtn.addEventListener('click', closeDrawer);

    headerContainer.appendChild(title);
    headerContainer.appendChild(closeBtn);

    return headerContainer;
  }

  const spacer = document.createElement('div');
  spacer.className = 'nde-c-side-menu__mp-spacer';

  return spacer;
};

/**
 * 이후 depth 패널 제거 및 형제 비활성화
 */
const removeDeeperPanels = (depth: number): void => {
  if (!state.drawerContent) {
    return;
  }

  const allPanels = $$<HTMLElement>('.nde-c-side-menu__mp-panel', state.drawerContent);
  for (const p of allPanels) {
    if (parseInt(p.dataset.depth || '0', 10) > depth) {
      p.remove();
    }
  }
};

const deactivateSiblingLinks = (ul: HTMLElement): void => {
  const siblings = $$<HTMLElement>('.nde-c-side-menu__mp-link', ul);
  for (const btn of siblings) {
    btn.classList.remove('nde-c-side-menu__mp-link--active');

    const icon = $<HTMLElement>('.arrow-icon', btn);
    if (icon) {
      icon.innerHTML = ICONS.chevron('#000');
    }
  }
};

/**
 * 패널 아이템 클릭 핸들러
 */
const handlePanelItemClick = ({
  button,
  item,
  depth,
  isFirstPanel,
  ul,
}: {
  button: HTMLElement;
  item: Category;
  depth: number;
  isFirstPanel: boolean;
  ul: HTMLElement;
}): void => {
  removeDeeperPanels(depth);
  deactivateSiblingLinks(ul);

  button.classList.add('nde-c-side-menu__mp-link--active');

  if (isFirstPanel) {
    const icon = $<HTMLElement>('.arrow-icon', button);
    if (icon) {
      icon.innerHTML = ICONS.chevron('#fff');
    }
  }

  const newPanel = createPanel(item.children ?? [], depth + 1);
  state.drawerContent?.appendChild(newPanel);
  updateDrawerWidth();
};

/**
 * 패널 아이템 요소 생성
 */
const createPanelItemElement = ({
  item,
  depth,
  isFirstPanel,
  ul,
}: {
  item: Category;
  depth: number;
  isFirstPanel: boolean;
  ul: HTMLElement;
}): HTMLElement => {
  const li = document.createElement('li');
  li.className = 'nde-c-side-menu__mp-item';

  const hasChildren = item.children && item.children.length > 0;
  const button = document.createElement(hasChildren ? 'button' : 'a') as HTMLButtonElement | HTMLAnchorElement;
  button.className = 'nde-c-side-menu__mp-link';
  button.classList.add(isFirstPanel ? 'nde-c-side-menu__mp-link--depth-0' : 'nde-c-side-menu__mp-link--depth-n');

  if (!hasChildren) {
    (button as HTMLAnchorElement).href = item.href;
  } else {
    (button as HTMLButtonElement).type = 'button';
  }

  const label = document.createElement('span');
  label.textContent = item.label;
  label.style.flex = '1';
  button.appendChild(label);

  if (hasChildren) {
    const arrowContainer = document.createElement('div');
    arrowContainer.className = 'nde-c-side-menu__mp-arrow arrow-icon';
    arrowContainer.innerHTML = ICONS.chevron('#000');
    button.appendChild(arrowContainer);

    button.addEventListener('click', (e) => {
      e.preventDefault();
      handlePanelItemClick({ button, item, depth, isFirstPanel, ul });
    });
  }

  li.appendChild(button);

  return li;
};

/**
 * 패널 생성
 */
const createPanel = (items: Category[], depth: number): HTMLElement => {
  const isFirstPanel = depth === 0;

  const panel = document.createElement('div');
  panel.className = 'nde-c-side-menu__mp-panel';
  panel.classList.add(isFirstPanel ? 'nde-c-side-menu__mp-panel--depth-0' : 'nde-c-side-menu__mp-panel--depth-n');
  panel.dataset.depth = String(depth);

  panel.appendChild(createPanelTopArea(isFirstPanel));

  const ul = document.createElement('ul');
  ul.className = 'nde-c-side-menu__mp-list';

  for (const item of items) {
    ul.appendChild(createPanelItemElement({ item, depth, isFirstPanel, ul }));
  }

  panel.appendChild(ul);
  return panel;
};

/**
 * 드로어 생성
 */
const createDrawer = (): HTMLElement => {
  if (state.drawer) {
    return state.drawer;
  }

  // 오버레이 생성
  state.overlay = document.createElement('div');
  state.overlay.className = 'nde-c-side-menu__drawer-overlay';
  document.body.appendChild(state.overlay);

  // 드로어 컨테이너 생성
  state.drawer = document.createElement('div');
  state.drawer.className = 'nde-c-side-menu__drawer nde-c-side-menu__drawer--multi-panel';

  // 드로어 콘텐츠 컨테이너 생성
  state.drawerContent = document.createElement('div');
  state.drawerContent.className = 'nde-c-side-menu__drawer-content nde-c-side-menu__drawer-content--multi-panel';

  state.drawer.appendChild(state.drawerContent);
  document.body.appendChild(state.drawer);

  // 오버레이 클릭 시 닫기
  state.overlay.addEventListener('click', closeDrawer);

  return state.drawer;
};

/**
 * 드로어 열기
 */
const openDrawer = (): void => {
  if (state.isDrawerOpen) {
    return;
  }

  // 진행중인 타임아웃 취소
  if (state.closeTimeoutId) {
    window.clearTimeout(state.closeTimeoutId);
    state.closeTimeoutId = null;
  }

  createDrawer();

  // 콘텐츠 및 너비 초기화
  if (state.drawerContent) {
    state.drawerContent.innerHTML = '';
  }
  if (state.drawer) {
    state.drawer.style.width = '';
  }

  // 첫 번째 패널 생성
  const firstPanel = createPanel(state.categories, 0);
  state.drawerContent?.appendChild(firstPanel);

  updateDrawerWidth();

  // 표시
  setTimeout(() => {
    state.overlay?.classList.add('nde-c-side-menu__drawer-overlay--visible');
    state.drawer?.classList.add('nde-c-side-menu__drawer--open');
  }, 10);

  state.isDrawerOpen = true;
  document.body.style.overflow = 'hidden';
};

/**
 * 카테고리 렌더링 (수평 리스트)
 */
const render = (list: HTMLElement, categories: Category[]): void => {
  // 카테고리를 state에 저장 (드로어에서 사용)
  state.categories = categories;

  list.innerHTML = categories
    .map((category) => {
      const activeClass = category.active ? ' nde-c-side-menu__link--active' : '';
      const targetAttr = category.isOpenNewTab ? ' target="_blank" rel="noopener noreferrer"' : '';
      return `
        <li class="nde-c-side-menu__item">
          <a href="${category.href}"${targetAttr} class="nde-c-side-menu__link${activeClass}">
            ${category.label}
          </a>
        </li>
      `;
    })
    .join('');
};

/**
 * 인터랙션 설정
 */
const setupInteractions = ({ elements, categories }: { elements: SideMenuElements; categories: Category[] }): void => {
  const { root } = elements;

  // 카테고리를 state에 저장 (드로어에서 사용)
  state.categories = categories;

  // 햄버거 버튼 설정
  const hamburgerButton = $<HTMLButtonElement>('.js-hamburger-toggle', root);
  if (hamburgerButton) {
    hamburgerButton.addEventListener('click', openDrawer);
  }

  // ESC 키 닫기
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && state.isDrawerOpen) {
      closeDrawer();
    }
  });
};

/**
 * 정리 작업
 */
const cleanup = (): void => {
  if (state.overlay) {
    state.overlay.remove();
    state.overlay = null;
  }
  if (state.drawer) {
    state.drawer.remove();
    state.drawer = null;
  }
  state.drawerContent = null;
  state.isDrawerOpen = false;
  state.categories = [];
};

export const drawerRenderer: SideMenuRenderer = {
  render,
  setupInteractions,
  cleanup,
};
